import { Alert, Box, Checkbox, FormControlLabel, Stack, Tooltip, Typography } from '@mui/material';

import type { IntakeSetupService } from './four-stage/IntakeSetupServicesPage';

export type SelectableIntakeService = IntakeSetupService;

export const INTAKE_SERVICE_OPTIONS: {
  id: SelectableIntakeService;
  label: string;
  description: string;
}[] = [
  { id: 'jotbill', label: 'JotBill', description: 'Set up billing for your practice.' },
  {
    id: 'jotassure',
    label: 'JotAssure',
    description: 'Certified claims for managed billing. Requires JotBill.',
  },
  { id: 'jotcred', label: 'JotCred', description: 'Prepare credentialing for your providers.' },
];

export function IntakeServiceSelectionCard({
  services,
  canManage,
  isSaving = false,
  allowJotAssure = true,
  onChange,
}: {
  services: SelectableIntakeService[];
  canManage: boolean;
  isSaving?: boolean;
  allowJotAssure?: boolean;
  onChange: (services: SelectableIntakeService[]) => void;
}) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography component="h2" sx={{ fontSize: '16px', fontWeight: 650, lineHeight: 1.5 }}>
          Services for your practice
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
          Choose JotBill or JotCred, with optional JotAssure for managed billing. Your selection
          determines the information your practice needs to provide.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        {INTAKE_SERVICE_OPTIONS.map(service => {
          const prerequisiteMissing =
            service.id === 'jotassure' && (!allowJotAssure || !services.includes('jotbill'));
          const explanation = prerequisiteMissing
            ? 'JotAssure requires JotBill managed billing. Go to Agreements & setup → Your services to update your selection, then review and sign any required agreements.'
            : '';
          return (
            <Tooltip key={service.id} title={explanation} describeChild>
              <Box tabIndex={prerequisiteMissing ? 0 : undefined} sx={{ display: 'flex' }}>
                <FormControlLabel
                  sx={{
                    m: 0,
                    width: '100%',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                    alignItems: 'center',
                  }}
                  control={
                    <Checkbox
                      slotProps={{ input: { 'aria-label': service.label } }}
                      size="small"
                      checked={services.includes(service.id)}
                      disabled={!canManage || isSaving || prerequisiteMissing}
                      onChange={(_event, checked) =>
                        onChange(
                          checked
                            ? [...services, service.id]
                            : services.filter(
                                id =>
                                  id !== service.id &&
                                  !(service.id === 'jotbill' && id === 'jotassure'),
                              ),
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.5 }}>
                        {service.label}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                        {service.description}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {!canManage && (
        <Alert severity="info">
          Ask your practice administrator to choose or change services for your organization.
        </Alert>
      )}
      {isSaving && (
        <Typography role="status" sx={{ fontSize: '14px' }}>
          Saving services…
        </Typography>
      )}
    </Stack>
  );
}
